import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, MoreHorizontal, Search } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { TablePagination } from './TablePagination';
export interface RowAction<T> {
  label: string;
  icon: React.ElementType;
  onClick: (row: T) => void;
  danger?: boolean;
}
export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (value: unknown, row: T) => ReactNode;
  sortable?: boolean;
  width?: string;
}
interface DataTableProps<T extends Record<string, unknown>> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  className?: string;
  rowActions?: RowAction<T>[];
  /** Enable built-in search filtering */
  searchable?: boolean;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Keys to search across (defaults to all string/number column keys) */
  searchKeys?: (keyof T)[];
  /** Enable pagination */
  pagination?: boolean;
  /** Default page size if pagination is enabled */
  pageSize?: number;
  /** Enable row selection */
  selectable?: boolean;
  /** Currently selected row IDs */
  selectedIds?: string[];
  /** Callback when selection changes */
  onSelectionChange?: (selectedIds: string[]) => void;
}
export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  emptyMessage = 'Geen gegevens beschikbaar',
  className = '',
  rowActions,
  searchable = false,
  searchPlaceholder = 'Zoeken...',
  searchKeys,
  pagination = false,
  pageSize = 50,
  selectable = false,
  selectedIds = [],
  onSelectionChange
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  // Filter data by search query
  const filteredData = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return data;
    const query = searchQuery.toLowerCase();
    const keys = searchKeys || columns.map(c => String(c.key));
    return data.filter(row => keys.some(key => {
      const val = row[String(key)];
      if (val == null) return false;
      return String(val).toLowerCase().includes(query);
    }));
  }, [data, searchable, searchQuery, searchKeys, columns]);
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortKey) return 0;
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (aVal === bVal) return 0;
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    const cmp = typeof aVal === 'number' && typeof bVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal), 'nl');
    return sortDir === 'asc' ? cmp : -cmp;
  });
  // Reset page when search changes
  useMemo(() => {
    setPage(0);
  }, [searchQuery]);
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    const start = page * currentPageSize;
    return sortedData.slice(start, start + currentPageSize);
  }, [sortedData, pagination, page, currentPageSize]);
  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (selectedIds.length === sortedData.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(sortedData.map((row, i) => String(row.id ?? i)));
    }
  };
  const handleSelectRow = (rowId: string) => {
    if (!onSelectionChange) return;
    if (selectedIds.includes(rowId)) {
      onSelectionChange(selectedIds.filter(id => id !== rowId));
    } else {
      onSelectionChange([...selectedIds, rowId]);
    }
  };
  return <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col ${className}`} data-id="element-7745" data-mp="10z3mxn8li3ey">
      {searchable && <div className="px-4 py-3 border-b border-gray-100" data-id="element-7746" data-mp="wvyobvrmylvl">
          <div className="w-full sm:max-w-sm" data-id="element-7747" data-mp="rz85x1scze70">
            <Input type="text" placeholder={searchPlaceholder} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-7749" data-mp="1kox6je15pc83" />} size="sm" data-id="element-7748" data-mp="s0032h1ltjze" />
          </div>
          {searchQuery && <div className="text-xs text-gray-400 mt-1.5" data-id="element-7750" data-mp="1awrk0jtobn0a">
              {filteredData.length} van {data.length} resultaten
            </div>}
        </div>}

      <div className="overflow-x-auto overflow-y-auto relative shadow-[inset_-12px_0_8px_-12px_rgba(0,0,0,0.1)] sm:shadow-none flex-1 max-h-[calc(100vh-280px)]" data-id="element-7751" data-mp="15vtz3zha4ev3">
        <table className="w-full text-sm min-w-[640px]" data-id="element-7752" data-mp="1yltxrgl6svpi">
          <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-100 shadow-sm" data-id="element-7753" data-mp="25y06bu0tg38z">
            <tr data-id="element-7754" data-mp="2awuwxi50yw3n">
              {selectable && <th className="px-4 py-3 w-12 text-left" data-id="element-7755" data-mp="2axd59d4yp0rx">
                  <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" checked={sortedData.length > 0 && selectedIds.length === sortedData.length} ref={input => {
                if (input) {
                  input.indeterminate = selectedIds.length > 0 && selectedIds.length < sortedData.length;
                }
              }} onChange={handleSelectAll} data-id="element-7756" data-mp="2fvenjvukcw2c" />
                </th>}
              {columns.map(col => <th key={String(col.key)} className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide ${col.sortable !== false ? 'cursor-pointer hover:text-gray-700 select-none' : ''}`} style={col.width ? {
              width: col.width
            } : undefined} onClick={() => col.sortable !== false && handleSort(String(col.key))} data-id="element-7757" data-mp="3cmq9hbtvhm1s">
                  <div className="flex items-center gap-1" data-id="element-7758" data-mp="1r8ln96kukl8h">
                    {col.label}
                    {col.sortable !== false && sortKey === String(col.key) && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" data-id="element-7759" data-mp="aubwk5t4ukeh" /> : <ChevronDown className="w-3 h-3" data-id="element-7760" data-mp="6t04zuqp7p3n" />)}
                  </div>
                </th>)}
              {rowActions && rowActions.length > 0 && <th className="px-4 py-3 w-10" data-id="element-7761" data-mp="qnfsx8glj5p"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50" data-id="element-7762" data-mp="3j4nz8r08vc5r">
            {paginatedData.length === 0 ? <tr data-id="element-7763" data-mp="3o3r5ufvvhqfu">
                <td colSpan={columns.length + (rowActions ? 1 : 0) + (selectable ? 1 : 0)} className="px-4 py-12 text-center text-sm text-gray-400" data-id="element-7764" data-mp="3o37ksqaxelxl">
                  {emptyMessage}
                </td>
              </tr> : paginatedData.map((row, i) => {
            const rowId = String(row.id ?? i);
            const isSelected = selectedIds.includes(rowId);
            return <tr key={rowId} onClick={() => onRowClick?.(row)} className={`transition-colors ${isSelected ? 'bg-blue-50/50' : ''} ${onRowClick ? 'cursor-pointer hover:bg-blue-50/50' : 'hover:bg-gray-50'}`} data-id="element-7765" data-mp="yfgsc7vkft0t" data-mpi={i}>
                    {selectable && <td className="px-4 py-3" onClick={e => e.stopPropagation()} data-id="element-7766" data-mp="26zmhy8mrjafs">
                        <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" checked={isSelected} onChange={() => handleSelectRow(rowId)} data-id="element-7767" data-mp="1y7c8itjoz8zl" />
                      </td>}
                    {columns.map(col => <td key={String(col.key)} className="px-4 py-3" data-id="element-7768" data-mp="1f8q6ncgcpdum">
                        {col.render ? col.render(row[String(col.key)], row) : <span className="text-gray-700" data-id="element-7769" data-mp="39n0upip4g3zc">
                            {String(row[String(col.key)] ?? '—')}
                          </span>}
                      </td>)}
                    {rowActions && rowActions.length > 0 && <td className="px-4 py-3 text-right relative" onClick={e => e.stopPropagation()} data-id="element-7770" data-mp="1551cn26o2g3w">
                        <button onClick={() => setOpenMenuId(openMenuId === rowId ? null : rowId)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-7771" data-mp="cfufo7mv1ykr">
                          <MoreHorizontal className="w-4 h-4" data-id="element-7772" data-mp="qgezn74ii0fu" />
                        </button>

                        {openMenuId === rowId && <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} data-id="element-7773" data-mp="21szbsi9sp5h0" />
                            <div className="absolute right-0 sm:right-8 top-10 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20" data-id="element-7774" data-mp="b5jd3n02aiuu">
                              {rowActions.map((action, idx) => {
                      const Icon = action.icon;
                      return <button key={idx} onClick={() => {
                        setOpenMenuId(null);
                        action.onClick(row);
                      }} className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left transition-colors ${action.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-7775" data-mp="3jf1py1arh8yp" data-mpi={idx}>
                                    <Icon className="w-4 h-4" data-id="element-7776" data-mp="2x5bhgvd3ewfx" />
                                    {action.label}
                                  </button>;
                    })}
                            </div>
                          </>}
                      </td>}
                  </tr>;
          })}
          </tbody>
        </table>
      </div>

      {pagination && <TablePagination page={page} pageSize={currentPageSize} total={sortedData.length} onPageChange={setPage} onPageSizeChange={size => {
      setCurrentPageSize(size);
      setPage(0);
    }} data-id="element-7777" data-mp="2ecgzrto34q80" />}
    </div>;
}